import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/portfolio/Navbar";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  Share2,
  Check,
  Sparkles,
  ExternalLink,
  ChevronRight,
  CheckSquare,
  Square,
  Zap,
  Layers,
  ArrowUpRight
} from "lucide-react";

export default function AiSearchCrawlingPostPage() {
  const [copied, setCopied] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});

  const toggleCheck = (id) => {
    setCheckedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    const originalTitle = document.title;
    const pageTitle = "Can AI Search Engines Crawl, Understand and Cite Your Website? | Kanna Narendra Kumar";
    const pageDesc = "Can AI search engines actually access, understand, retrieve, and cite your content? Start with technical accessibility before AI SEO, AEO, and GEO. Here is the checklist and 4-layer framework.";
    const pageUrl = "https://narendradigital.in/blog/can-ai-search-engines-crawl-understand-cite-website/";

    document.title = pageTitle;

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute(attr, value);
      } else {
        const meta = document.createElement("meta");
        const isProp = selector.includes("property=");
        if (isProp) {
          const match = selector.match(/property="([^"]+)"/);
          if (match) meta.setAttribute("property", match[1]);
        } else {
          const match = selector.match(/name="([^"]+)"/);
          if (match) meta.setAttribute("name", match[1]);
        }
        meta.setAttribute(attr, value);
        document.head.appendChild(meta);
      }
    };

    setMeta('meta[name="description"]', "content", pageDesc);
    setMeta('meta[property="og:title"]', "content", pageTitle);
    setMeta('meta[property="og:description"]', "content", pageDesc);
    setMeta('meta[property="og:url"]', "content", pageUrl);
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[property="og:image"]', "content", "https://narendradigital.in/og-image-v3.jpg");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", pageTitle);
    setMeta('meta[name="twitter:description"]', "content", pageDesc);
    setMeta('meta[name="twitter:image"]', "content", "https://narendradigital.in/og-image-v3.jpg");

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    let createdCanonical = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    const originalCanonical = canonical ? canonical.getAttribute("href") : null;
    canonical.setAttribute("href", pageUrl);

    // Inject JSON-LD Schema: TechArticle + BreadcrumbList
    const scriptId = "ai-crawling-article-schema";
    let scriptTag = document.getElementById(scriptId);
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = scriptId;
      scriptTag.type = "application/ld+json";
      scriptTag.text = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "TechArticle",
            "@id": `${pageUrl}#article`,
            "isPartOf": {
              "@type": "WebSite",
              "@id": "https://narendradigital.in/#website",
              "name": "Kanna Narendra Kumar — Senior SEO & AI Search Strategist",
              "url": "https://narendradigital.in"
            },
            "headline": "Can AI Search Engines Crawl, Understand and Cite Your Website?",
            "description": pageDesc,
            "image": "https://narendradigital.in/og-image-v3.jpg",
            "datePublished": "2026-09-20T10:00:00+05:30",
            "dateModified": "2026-09-20T22:30:00+05:30",
            "mainEntityOfPage": pageUrl,
            "author": {
              "@type": "Person",
              "@id": "https://narendradigital.in/#author",
              "name": "Kanna Narendra Kumar",
              "jobTitle": "Senior SEO Specialist & AI Search Strategist",
              "url": "https://narendradigital.in/",
              "sameAs": [
                "https://www.linkedin.com/in/narendra-kumar-kanna/"
              ]
            },
            "publisher": {
              "@type": "Person",
              "name": "Kanna Narendra Kumar"
            },
            "keywords": [
              "Can AI Search Engines Crawl Understand and Cite Your Website",
              "AI SEO",
              "AEO",
              "GEO",
              "LLM optimization",
              "entity optimization",
              "AI content strategies",
              "robots.txt AI crawlers",
              "OAI-SearchBot",
              "HTTP 200 OK",
              "AI visibility framework"
            ],
            "articleSection": "Technical SEO & AI Search",
            "inLanguage": "en-US"
          },
          {
            "@type": "BreadcrumbList",
            "@id": `${pageUrl}#breadcrumb`,
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://narendradigital.in/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://narendradigital.in/blog/"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Can AI Search Engines Crawl, Understand and Cite Your Website?",
                "item": pageUrl
              }
            ]
          }
        ]
      });
      document.head.appendChild(scriptTag);
    }

    window.scrollTo(0, 0);

    return () => {
      document.title = originalTitle;
      if (canonical) {
        if (createdCanonical) {
          canonical.remove();
        } else if (originalCanonical) {
          canonical.setAttribute("href", originalCanonical);
        }
      }
      if (scriptTag) scriptTag.remove();
    };
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const technicalChecklist = [
    "Important URLs return 200 OK",
    "HTTPS is correctly implemented",
    "No unexpected redirect chains",
    "CDN/WAF isn't blocking legitimate crawlers",
    "robots.txt has been reviewed",
    "Relevant AI/search crawlers are intentionally allowed or blocked",
    "Important pages aren't accidentally noindex",
    "X-Robots-Tag is checked",
    "Canonical URLs are correct",
    "Important pages have internal links",
    "XML sitemap is accessible",
    "Important content is accessible to crawlers/renderers",
    "JavaScript isn't preventing access to critical content",
    "Structured data accurately represents the page",
    "Server logs have been reviewed"
  ];

  const contentChecklist = [
    "The primary topic is immediately clear",
    "The page answers a specific user question",
    "Important information isn't unnecessarily hidden",
    "Headings clearly organize the topic",
    "Related entities and concepts are explained",
    "Relevant internal links connect supporting content",
    "Important claims are supported with evidence",
    "The page provides unique value"
  ];

  const retrievalChecklist = [
    "Test distinctive content excerpts",
    "Test branded queries",
    "Test non-branded informational queries",
    "Test question-based queries",
    "Check whether your URL appears in AI answers",
    "Record which queries produce citations",
    "Repeat testing periodically"
  ];

  const totalChecklistCount = technicalChecklist.length + contentChecklist.length + retrievalChecklist.length;
  const completedCount = Object.values(checkedItems).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#070a12] text-white selection:bg-cyan-500 selection:text-black">
      {/* Background Glows */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-indigo-600/10 rounded-full blur-[180px] pointer-events-none" />
      <div className="fixed top-96 right-10 w-[500px] h-[350px] bg-cyan-600/10 rounded-full blur-[160px] pointer-events-none" />

      {/* Main Global Navigation Bar (Original Site Navbar) */}
      <Navbar />

      <main className="max-w-4xl lg:max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14 relative z-10">
        {/* Article Breadcrumbs & Quick Utility Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <nav className="flex items-center gap-2 text-xs font-mono text-white/50">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3 text-white/30" />
            <Link to="/blog/" className="hover:text-white transition-colors">Blog</Link>
            <ChevronRight className="w-3 h-3 text-white/30" />
            <span className="text-cyan-400 truncate">AI SEO &amp; Crawling</span>
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/blog/"
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-white/70 hover:text-cyan-400 px-3 py-1.5 rounded-full border border-white/10 hover:border-cyan-500/30 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Lab</span>
            </Link>

            <button
              onClick={handleCopyLink}
              className="px-3 py-1.5 rounded-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-xs font-mono text-white/80 flex items-center gap-1.5 transition-colors cursor-pointer"
              title="Copy Article URL"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5" />}
              <span>{copied ? "Copied" : "Share"}</span>
            </button>
          </div>
        </div>

        {/* Article Hero */}
        <header className="space-y-6 pb-8 border-b border-white/10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-xs font-mono uppercase tracking-widest text-cyan-400">
            <Sparkles className="w-3.5 h-3.5" />
            <span>AI Search &amp; Technical SEO</span>
          </div>

          <h1 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl text-white tracking-tight leading-tight">
            Can AI Search Engines Crawl, Understand and Cite Your Website?
          </h1>

          {/* Author Metadata Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-white/[0.08]">
            <div className="flex items-center gap-3.5">
              <img
                src="/narendra-workstation.jpg"
                alt="Kanna Narendra Kumar"
                className="w-12 h-12 rounded-xl object-cover object-[center_20%] border border-cyan-500/30 shadow-md"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/narendra-avatar.jpg";
                }}
              />
              <div>
                <div className="font-heading font-bold text-sm text-white">
                  Kanna Narendra Kumar
                </div>
                <div className="text-[11px] text-white/50 font-mono">
                  Senior SEO Specialist &amp; AI Search Strategist
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 text-xs text-white/60 font-mono">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-cyan-400" />
                <span>Sep 20, 2026</span>
              </span>
              <span>&bull;</span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-indigo-400" />
                <span>8 min read</span>
              </span>
            </div>
          </div>
        </header>

        {/* Key Takeaways Box — Distinct Callout Container with Clean Semantic Pointers for AI Bots & Readers */}
        <section
          aria-label="Key Takeaways"
          className="my-7 p-5 sm:p-6 rounded-xl bg-[#0c1626] border-2 border-cyan-400 shadow-lg shadow-cyan-950/20"
        >
          <div className="flex items-center gap-2 mb-3.5 text-cyan-400">
            <Sparkles className="w-4 h-4 shrink-0 text-cyan-400" />
            <h2 className="text-xs sm:text-sm font-mono font-bold uppercase tracking-wider text-cyan-400 m-0">
              Key Takeaways
            </h2>
          </div>

          <ul className="list-disc pl-5 space-y-2.5 text-sm sm:text-[15px] text-slate-200 leading-relaxed marker:text-cyan-400">
            <li>
              <strong className="text-white">Technical foundation comes first:</strong> Before investing in AI SEO, AEO, or GEO prompt tactics, verify your website returns <code className="text-cyan-300 bg-cyan-950/70 border border-cyan-500/30 px-1.5 py-0.5 rounded text-xs font-mono">200 OK</code> without WAF bot-blocks, CAPTCHA hurdles, or authentication barriers.
            </li>
            <li>
              <strong className="text-white">The 5-stage AI visibility pipeline:</strong> Content progresses strictly through <code className="text-cyan-300 bg-cyan-950/70 border border-cyan-500/30 px-1.5 py-0.5 rounded text-xs font-mono">ACCESS → DISCOVER → UNDERSTAND → RETRIEVE → CITE</code>. Failures at earlier access layers prevent generative citations entirely.
            </li>
            <li>
              <strong className="text-white">Intentional crawler permissions:</strong> Actively audit <code className="text-cyan-300 bg-cyan-950/70 border border-cyan-500/30 px-1.5 py-0.5 rounded text-xs font-mono">robots.txt</code> and firewall configurations to ensure discovery bots like OpenAI’s <code className="text-cyan-300 bg-cyan-950/70 border border-cyan-500/30 px-1.5 py-0.5 rounded text-xs font-mono">OAI-SearchBot</code> and ClaudeBot are permitted to crawl your assets.
            </li>
            <li>
              <strong className="text-white">Machine readability without JavaScript reliance:</strong> Ensure main text, heading hierarchy, and Schema.org structured data are directly accessible in the initial HTML response rather than hidden behind client-side rendering.
            </li>
            <li>
              <strong className="text-white">Crawl requests do not equal citations:</strong> Server log hits only prove a network request occurred—they do not guarantee an AI engine parsed, indexed, or cited the page in generative answers.
            </li>
          </ul>
        </section>

        {/* Article Body — 100% Original Content Preserved Word-for-Word */}
        <article className="py-10 space-y-8 text-white/85 text-base sm:text-lg leading-relaxed font-sans">
          
          <div className="space-y-4">
            <p>You have published great content.</p>
            <p>Your website is technically optimized.</p>
            <p>Your pages are indexed by Google.</p>
            <p>But there is another question you should be asking:</p>

            <blockquote className="p-5 sm:p-6 rounded-2xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-semibold text-lg sm:text-xl shadow-xl my-6 not-italic">
              Can AI search engines actually access, understand, retrieve, and cite your content?
            </blockquote>

            <p>
              As search evolves from traditional blue links to AI-generated answers, this question is becoming increasingly important.
            </p>

            <p>However, there is a common mistake I see in AI SEO.</p>

            <p>We jump directly into:</p>

            <ul className="list-disc pl-6 space-y-2 text-white/90">
              <li>AI SEO</li>
              <li>AEO</li>
              <li>GEO</li>
              <li>LLM optimization</li>
              <li>entity optimization</li>
              <li>AI content strategies</li>
            </ul>

            <p>
              without first checking whether the technical foundation allows machines to access the content in the first place.
            </p>

            <p>
              Before asking <strong>“How do I get cited by AI?”</strong>, start with:
            </p>

            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-indigo-400 text-white font-medium not-italic my-4">
              “Can the systems I care about actually access and understand my content?”
            </blockquote>

            <p>Here is the checklist I use.</p>
          </div>

          <hr className="border-white/10 my-8" />

          {/* Section: The AI Content Accessibility Checklist */}
          <div className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              The AI Content Accessibility Checklist
            </h2>

            <p>Think about AI visibility as a journey:</p>

            <div className="p-4 rounded-xl bg-[#080d19] border border-cyan-500/30 font-mono text-xs sm:text-sm text-cyan-300 font-bold text-center tracking-wide overflow-x-auto">
              ACCESS &rarr; DISCOVER &rarr; UNDERSTAND &rarr; RETRIEVE &rarr; CITE
            </div>

            <p>
              If you have a problem at the first stage, optimizing the final stage becomes much harder.
            </p>
          </div>

          <hr className="border-white/10 my-8" />

          {/* 1. Does Your Page Return a Successful HTTP Response? */}
          <section className="space-y-4">
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-white tracking-tight">
              1. Does Your Page Return a Successful HTTP Response?
            </h2>

            <p>Start with the basics.</p>
            <p>Your important pages should be accessible and return an appropriate HTTP status.</p>
            <p>
              For a normal indexable webpage, you generally want the final URL to return:
            </p>

            <p className="font-mono text-emerald-400 text-xl font-bold">200 OK</p>

            <p>Check for:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-white/80">
              <li>404 errors</li>
              <li>410 responses</li>
              <li>403 Forbidden</li>
              <li>5xx server errors</li>
              <li>unexpected redirect chains</li>
              <li>HTTP &rarr; HTTPS issues</li>
              <li>CDN or WAF blocking</li>
              <li>authentication requirements</li>
            </ul>

            <p>
              A page can be perfectly optimized from a content perspective and still be inaccessible to a crawler because your infrastructure is returning a <code className="bg-white/10 px-1.5 py-0.5 rounded text-cyan-300 text-sm font-mono">403</code>, challenging the bot with a CAPTCHA, or blocking automated traffic.
            </p>

            <p>
              OpenAI's current crawler guidance, for example, specifically recommends checking robots.txt, WAF/CDN rules, bot mitigation, authentication, JavaScript challenges and rate limiting when its crawlers cannot access a page.
            </p>

            <h3 className="font-heading font-bold text-lg text-white pt-2">What to check</h3>

            <pre className="p-4 rounded-xl bg-[#080d19] border border-white/10 text-cyan-300 font-mono text-xs sm:text-sm leading-relaxed overflow-x-auto">
{`URL
 ↓
HTTP request
 ↓
200 OK?
 ↓
Content accessible?`}
            </pre>

            <p>
              If the answer stops at the HTTP layer, there is no point discussing content optimization yet.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* 2. Is Your robots.txt Blocking Relevant AI Crawlers? */}
          <section className="space-y-4">
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-white tracking-tight">
              2. Is Your robots.txt Blocking Relevant AI Crawlers?
            </h2>

            <p>Go to:</p>
            <pre className="p-3 rounded-xl bg-[#080d19] border border-white/10 font-mono text-xs sm:text-sm text-cyan-300 overflow-x-auto">
https://example.com/robots.txt
            </pre>

            <p>Then check whether your important content is being blocked.</p>
            <p>For example:</p>

            <pre className="p-3 rounded-xl bg-[#080d19] border border-rose-500/30 font-mono text-xs sm:text-sm text-rose-300 overflow-x-auto">
{`User-agent: *
Disallow: /`}
            </pre>

            <p>is effectively telling compliant crawlers not to crawl the entire site.</p>
            <p>But there is an important distinction here.</p>

            <h3 className="font-heading font-bold text-lg text-white">
              Don't automatically allow every AI crawler.
            </h3>

            <p>Different crawlers can serve different purposes.</p>
            <p>
              Some are associated with search discovery, while others may serve different functions such as training, research or product experiences.
            </p>
            <p>Your crawler policy should therefore reflect your publishing strategy.</p>

            <p>
              For example, OpenAI currently documents <strong>OAI-SearchBot</strong> and states that publishers who want their content to be discoverable and surfaced in ChatGPT should avoid blocking it.
            </p>

            <p>So instead of asking:</p>
            <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-white/40 text-white/80 not-italic">
              “Have I allowed all AI bots?”
            </blockquote>

            <p>Ask:</p>
            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-200 font-semibold not-italic">
              “Have I intentionally decided which AI crawlers should be allowed to access my content?”
            </blockquote>

            <p>That is a much better SEO question.</p>
          </section>


          <hr className="border-white/10 my-8" />

          {/* 3. Is Your Page Actually Indexable? */}
          <section className="space-y-4">
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-white tracking-tight">
              3. Is Your Page Actually Indexable?
            </h2>

            <p>Crawling and indexing are not the same thing.</p>
            <p>A crawler may be able to access your page while the page is still prevented from being indexed.</p>

            <p>Check for:</p>
            <pre className="p-3 rounded-xl bg-[#080d19] border border-white/10 font-mono text-xs sm:text-sm text-cyan-300 overflow-x-auto">
{`<meta name="robots" content="noindex">`}
            </pre>

            <p>Also check:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-white/80">
              <li><code className="bg-white/10 px-1.5 py-0.5 rounded text-cyan-300 text-sm font-mono">X-Robots-Tag</code></li>
              <li>robots.txt</li>
              <li>canonical implementation</li>
              <li>authentication</li>
              <li>sitemap inclusion</li>
              <li>internal links</li>
              <li>Google Search Console indexability</li>
              <li>URL Inspection</li>
            </ul>

            <p>
              Google's documentation recommends checking that pages are accessible and not blocked by robots.txt, <code className="bg-white/10 px-1.5 py-0.5 rounded text-cyan-300 text-sm font-mono">noindex</code>, or login requirements when validating how Google sees a page.
            </p>

            <p>This leads to a simple principle:</p>

            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-cyan-400 text-white font-semibold not-italic">
              “If you don't want a page discoverable through search, don't assume an AI system will treat it as a normal public source either.”
            </blockquote>

            <p>
              There are differences between individual AI systems, so indexing should not be treated as a universal prerequisite for every AI product.
            </p>
            <p>
              But for organic search visibility, it remains a fundamental technical check.
            </p>
          </section>

          {/* Mid-Article AI Visibility Audit CTA Callout (~30% Scroll) */}
          <div className="my-8 p-6 sm:p-7 rounded-2xl bg-gradient-to-r from-[#0c1629] via-[#0f1d38] to-[#0c1426] border border-cyan-500/35 shadow-xl shadow-cyan-950/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 not-prose">
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold text-cyan-400 uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span>AI Search Readiness Check</span>
              </div>
              <h3 className="font-heading font-bold text-lg sm:text-xl text-white leading-snug">
                Can AI Search Engines Crawl, Understand &amp; Cite Your Website?
              </h3>
              <p className="text-xs sm:text-sm text-white/70 max-w-xl leading-relaxed">
                Get a complete 4-layer AI Visibility Audit across Accessibility, Discoverability, Understanding, and Citations for ChatGPT Search, Google AI Overviews, and Claude.
              </p>
            </div>
            <Link
              to="/ai-visibility-audit/"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/25 hover:scale-[1.02] transition-all"
            >
              <span>Get an AI Visibility Audit</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <hr className="border-white/10 my-8" />

          {/* 4. Is Your Canonical URL Correct? */}
          <section className="space-y-4">
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-white tracking-tight">
              4. Is Your Canonical URL Correct?
            </h2>

            <p>This is where a common SEO misconception needs correcting.</p>
            <p>A canonical tag does <strong>not</strong> mean:</p>
            <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-white/40 text-white/80 not-italic">
              “This is my most important page.”
            </blockquote>

            <p>
              Instead, canonicalization tells search engines which URL you consider the representative version when multiple URLs contain duplicate or substantially similar content.
            </p>
            <p>For example:</p>

            <pre className="p-3 rounded-xl bg-[#080d19] border border-white/10 font-mono text-xs sm:text-sm text-cyan-300 overflow-x-auto">
{`<link rel="canonical" href="https://example.com/technical-seo/">`}
            </pre>

            <p>A self-referencing canonical is common for a standalone preferred URL.</p>
            <p>But don't add self-canonicals blindly.</p>

            <p>Ask:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-white/80">
              <li>Is this the preferred URL?</li>
              <li>Are there duplicate URLs?</li>
              <li>Are URL parameters creating variations?</li>
              <li>Are HTTP and HTTPS versions consolidated?</li>
              <li>Are localized URLs implemented correctly?</li>
              <li>Are trailing-slash variations handled consistently?</li>
              <li>Is JavaScript changing the canonical incorrectly?</li>
            </ul>

            <p>
              Google's current documentation specifically highlights the importance of making canonicalization clear in the original HTML, especially when JavaScript is involved.
            </p>

            <p>So the question isn't:</p>
            <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-white/40 text-white/80 not-italic">
              “Does every page have a self-canonical?”
            </blockquote>

            <p>The better question is:</p>
            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-200 font-semibold not-italic">
              “Have I clearly communicated which URL represents this content?”
            </blockquote>
          </section>

          <hr className="border-white/10 my-8" />

          {/* 5. Can Search Engines and AI Systems Discover Your Important Pages Through Internal Links? */}
          <section className="space-y-4">
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-white tracking-tight">
              5. Can Search Engines and AI Systems Discover Your Important Pages Through Internal Links?
            </h2>

            <p>A page can exist on your website and still be difficult to discover.</p>
            <p>Look at your internal linking structure.</p>
            <p>Your important pages should be connected logically to other relevant pages.</p>
            <p>For example:</p>

            <pre className="p-4 rounded-xl bg-[#080d19] border border-white/10 text-cyan-300 font-mono text-xs sm:text-sm leading-relaxed overflow-x-auto">
{`SEO
│
├── Technical SEO
│   ├── Crawlability
│   ├── Indexability
│   └── JavaScript SEO
│
├── Content SEO
│   ├── Search Intent
│   ├── Keyword Research
│   └── Content Optimization
│
└── AI Search Optimization
    ├── AI Crawlers
    ├── AI Visibility
    └── AI Citations`}
            </pre>

            <p>This creates contextual relationships between your content.</p>
            <p>But there is another common mistake.</p>

            <h3 className="font-heading font-bold text-lg text-white">
              Don't force keywords into every anchor text.
            </h3>

            <p>
              Your internal links should use descriptive anchor text that accurately describes the destination.
            </p>
            <p>For example:</p>

            <p><strong>Good:</strong></p>
            <blockquote className="p-3 rounded-xl bg-emerald-950/20 border-l-4 border-emerald-400 text-emerald-200 not-italic text-sm sm:text-base">
              Learn more about JavaScript SEO
            </blockquote>

            <p><strong>Weak:</strong></p>
            <blockquote className="p-3 rounded-xl bg-rose-950/20 border-l-4 border-rose-400 text-rose-200 not-italic text-sm sm:text-base">
              Click here
            </blockquote>

            <p>The objective isn't to create keyword-stuffed anchors.</p>
            <p>
              The objective is to create <strong>clear relationships between relevant pieces of content</strong>.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* 6. Can Your Important Content Be Accessed Without Depending Entirely on Client-Side JavaScript? */}
          <section className="space-y-4">
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-white tracking-tight">
              6. Can Your Important Content Be Accessed Without Depending Entirely on Client-Side JavaScript?
            </h2>

            <p>This topic needs some nuance.</p>
            <p>You may have heard:</p>
            <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-white/40 text-white/80 not-italic">
              “AI bots can't read JavaScript.”
            </blockquote>

            <p>That is too simplistic.</p>
            <p>
              Google has been rendering JavaScript for years, and its current documentation explicitly notes that JavaScript itself does not automatically make content inaccessible to Google Search.
            </p>
            <p>
              However, that doesn't mean you should make important content unnecessarily dependent on complex client-side rendering.
            </p>

            <p>Check:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-white/80">
              <li>Is the main content present?</li>
              <li>Are headings available?</li>
              <li>Are important links available?</li>
              <li>Is content loaded through an API?</li>
              <li>Does content appear only after user interaction?</li>
              <li>Does the content disappear when JavaScript fails?</li>
              <li>Are there rendering errors?</li>
              <li>Are important sections hidden behind client-side components?</li>
            </ul>

            <p>The principle is simple:</p>
            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-semibold not-italic">
              “If the content is important, make sure it is reliably accessible to crawlers and renderers.”
            </blockquote>
          </section>

          <hr className="border-white/10 my-8" />

          {/* 7. Is Your Structured Data Helping Machines Understand the Page? */}
          <section className="space-y-4">
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-white tracking-tight">
              7. Is Your Structured Data Helping Machines Understand the Page?
            </h2>

            <p>Structured data can provide additional machine-readable context about your content.</p>
            <p>Depending on the page, this could include:</p>

            <ul className="list-disc pl-6 space-y-1.5 text-white/80">
              <li>Article</li>
              <li>BreadcrumbList</li>
              <li>Organization</li>
              <li>Person</li>
              <li>Product</li>
              <li>ProfilePage</li>
              <li>other relevant Schema.org types</li>
            </ul>

            <p>For example:</p>

            <pre className="p-4 rounded-xl bg-[#080d19] border border-white/10 text-cyan-300 font-mono text-xs sm:text-sm overflow-x-auto">
{`{
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Can AI Search Engines Crawl, Understand and Cite Your Website?",
  "author": {
    "@type": "Person",
    "name": "Your Name"
  }
}`}
            </pre>

            <p>But remember:</p>
            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-amber-400 text-amber-200 font-semibold not-italic">
              “Structured data is not a replacement for your actual content.”
            </blockquote>

            <p>
              Don't put valuable information only inside JSON-LD and assume that adding schema makes the information available to AI systems.
            </p>
            <p>
              Google describes structured data as a standardized format for providing information about and classifying page content. It also recommends validating structured data and using URL Inspection to test how Google sees the page.
            </p>
            <p>Your visible content still matters.</p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* 8. What Do Your Server Logs Tell You? */}
          <section className="space-y-4">
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-white tracking-tight">
              8. What Do Your Server Logs Tell You?
            </h2>

            <p>This is one of the most underrated SEO checks.</p>
            <p>Instead of assuming:</p>
            <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-white/40 text-white/80 not-italic">
              “I allowed AI bots, so they must be crawling my website.”
            </blockquote>

            <p>Look at the evidence.</p>
            <p>Your server logs can potentially show:</p>

            <ul className="list-disc pl-6 space-y-1.5 text-white/80">
              <li>User agent</li>
              <li>Requested URL</li>
              <li>HTTP status</li>
              <li>Response time</li>
              <li>Crawl frequency</li>
              <li>Response size</li>
              <li>Blocked requests</li>
              <li>Repeated errors</li>
            </ul>

            <p>You might discover something like:</p>

            <pre className="p-4 rounded-xl bg-[#080d19] border border-white/10 text-cyan-300 font-mono text-xs sm:text-sm overflow-x-auto">
{`Crawler → /blog/article-1 → 200
Crawler → /blog/article-2 → 403
Crawler → /blog/article-3 → 200
Crawler → /blog/article-4 → 500`}
            </pre>

            <p>That gives you a much clearer picture of what your infrastructure is actually serving.</p>
            <p>But there is an important limitation.</p>

            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-semibold not-italic">
              “A crawler request does not prove that an AI system understood, indexed, retrieved or cited your content.”
            </blockquote>

            <p>It only proves that a request was made.</p>
            <p>That distinction is extremely important when measuring AI visibility.</p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* The 30-Second AI Retrieval Sanity Check */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              The 30-Second AI Retrieval Sanity Check
            </h2>

            <p>Now for a simple test you can perform yourself.</p>
            <p>Find a distinctive passage from the page you want to test.</p>
            <p>Take approximately <strong>30–40 words</strong>.</p>
            <p>Then ask an AI system:</p>

            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-indigo-400 text-white font-semibold not-italic text-lg">
              “Find this exact content on the internet and give me the URL of the page where it appears.”
            </blockquote>

            <p>For example, if your website contains:</p>
            <blockquote className="p-4 rounded-xl bg-[#080d19] border border-white/10 text-white/80 italic">
              “Hybrid Engine Optimization combines traditional search optimization with answer-oriented optimization to improve visibility across search engines and AI-powered discovery systems.”
            </blockquote>

            <p>Copy a distinctive section and run the test.</p>

            <h3 className="font-heading font-bold text-lg text-emerald-400 pt-2">
              If the AI returns your URL
            </h3>
            <p>
              You have evidence that the content is discoverable through that particular system at that moment.
            </p>

            <h3 className="font-heading font-bold text-lg text-rose-400 pt-2">
              If it doesn't return your URL
            </h3>
            <p>Don't immediately conclude that:</p>
            <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-white/40 text-white/80 not-italic">
              “The AI can't read my website.”
            </blockquote>

            <p>There could be several reasons.</p>
            <p>The result can depend on:</p>

            <ul className="list-disc pl-6 space-y-1.5 text-white/80">
              <li>the AI system</li>
              <li>retrieval technology</li>
              <li>search index freshness</li>
              <li>query wording</li>
              <li>geographic availability</li>
              <li>crawl freshness</li>
              <li>whether the page has been discovered</li>
              <li>whether another website has reproduced similar content</li>
            </ul>

            <p>So treat this as a:</p>
            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-200 font-semibold not-italic">
              “Retrieval sanity check — not a definitive AI crawlability test.”
            </blockquote>
          </section>

          <hr className="border-white/10 my-8" />

          {/* But Here's the Important Part */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              But Here's the Important Part
            </h2>

            <p>Finding your URL does <strong>not</strong> automatically mean:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-white/80">
              <li>the AI has permanently indexed your page</li>
              <li>the AI crawler directly visited your page</li>
              <li>your page will be cited for relevant questions</li>
              <li>the AI understands your entire article</li>
              <li>your website has strong AI visibility</li>
            </ul>

            <p>
              Likewise, not finding your URL does not automatically mean your website is technically inaccessible.
            </p>
            <p>This is why I separate AI visibility into four stages.</p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* The Four Layers of AI Visibility */}
          <section className="space-y-6">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              The Four Layers of AI Visibility
            </h2>

            <div className="space-y-3">
              <h3 className="font-heading font-bold text-xl text-cyan-400">
                Layer 1 — Accessibility
              </h3>
              <p className="font-semibold text-white">Can the system reach your content?</p>
              <p className="text-white/70">Check:</p>
              <ul className="list-disc pl-6 space-y-1 text-white/80">
                <li>HTTP status</li>
                <li>HTTPS</li>
                <li>robots.txt</li>
                <li>WAF/CDN</li>
                <li>authentication</li>
                <li>bot protection</li>
              </ul>
            </div>

            <div className="text-cyan-400 font-bold text-xl">&darr;</div>

            <div className="space-y-3">
              <h3 className="font-heading font-bold text-xl text-indigo-400">
                Layer 2 — Discoverability
              </h3>
              <p className="font-semibold text-white">Can the system discover your content?</p>
              <p className="text-white/70">Check:</p>
              <ul className="list-disc pl-6 space-y-1 text-white/80">
                <li>internal links</li>
                <li>XML sitemap</li>
                <li>crawl paths</li>
                <li>canonicalization</li>
                <li>external references</li>
              </ul>
            </div>

            <div className="text-cyan-400 font-bold text-xl">&darr;</div>

            <div className="space-y-3">
              <h3 className="font-heading font-bold text-xl text-purple-400">
                Layer 3 — Understanding
              </h3>
              <p className="font-semibold text-white">Can the system clearly understand what your content is about?</p>
              <p className="text-white/70">Check:</p>
              <ul className="list-disc pl-6 space-y-1 text-white/80">
                <li>page structure</li>
                <li>headings</li>
                <li>semantic relationships</li>
                <li>structured data</li>
                <li>entities</li>
                <li>clear explanations</li>
                <li>visible content</li>
              </ul>
            </div>

            <div className="text-cyan-400 font-bold text-xl">&darr;</div>

            <div className="space-y-3">
              <h3 className="font-heading font-bold text-xl text-emerald-400">
                Layer 4 — Retrieval &amp; Citation
              </h3>
              <p className="font-semibold text-white">Does the system retrieve your page when users ask relevant questions?</p>
              <p className="text-white/70">Check:</p>
              <ul className="list-disc pl-6 space-y-1 text-white/80">
                <li>branded queries</li>
                <li>informational queries</li>
                <li>question-based queries</li>
                <li>citation frequency</li>
                <li>URL visibility</li>
                <li>source selection</li>
              </ul>
            </div>

            <p className="pt-2">This is where many AI SEO strategies jump too quickly.</p>
            <p>
              They start at <strong>Layer 4</strong> without validating Layers 1–3.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* What If Your Website Is Crawlable but Still Isn't Being Cited? */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              What If Your Website Is Crawlable but Still Isn't Being Cited?
            </h2>

            <p>This is where the problem becomes more interesting.</p>
            <p>Suppose you have already confirmed:</p>

            <div className="p-3 rounded-xl bg-[#080d19] border border-cyan-500/30 font-mono text-xs sm:text-sm text-cyan-300 font-bold text-center overflow-x-auto">
              200 OK &rarr; Crawlable &rarr; Indexable &rarr; Correct canonical &rarr; Internally linked &rarr; Content accessible
            </div>

            <p>But your pages still don't appear as sources in AI answers.</p>
            <p>Then the problem may no longer be purely technical.</p>
            <p>Start investigating:</p>

            <div className="space-y-4 pt-2">
              <div>
                <h3 className="font-heading font-bold text-lg text-white">1. Topical relevance</h3>
                <p className="text-white/80">Does your page actually answer the question being asked?</p>
              </div>

              <div>
                <h3 className="font-heading font-bold text-lg text-white">2. Information completeness</h3>
                <p className="text-white/80">Does it provide a complete answer, or does the user need to visit five other pages?</p>
              </div>

              <div>
                <h3 className="font-heading font-bold text-lg text-white">3. Content structure</h3>
                <p className="text-white/80">Can important facts and concepts be extracted easily?</p>
              </div>

              <div>
                <h3 className="font-heading font-bold text-lg text-white">4. Entity clarity</h3>
                <p className="text-white/80">Is it obvious who created the content and what the page is about?</p>
              </div>

              <div>
                <h3 className="font-heading font-bold text-lg text-white">5. Evidence</h3>
                <p className="text-white/80">Are important claims supported by data, examples, original research or credible references?</p>
              </div>

              <div>
                <h3 className="font-heading font-bold text-lg text-white">6. External references</h3>
                <p className="text-white/80">Are other relevant websites mentioning, referencing or citing your brand?</p>
              </div>

              <div>
                <h3 className="font-heading font-bold text-lg text-white">7. Original value</h3>
                <p className="text-white/80">Does your page provide something genuinely useful that isn't simply repeating the same information available everywhere else?</p>
              </div>
            </div>

            <p className="pt-2">
              This is where technical SEO starts connecting with <strong>AEO, GEO and broader AI visibility optimization</strong>.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* My Practical AI Accessibility Checklist (Interactive Tracking) */}
          <section id="practical-checklist" className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-white/10">
              <div>
                <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
                  My Practical AI Accessibility Checklist
                </h2>
                <p className="text-sm text-white/70">
                  Before starting an AI visibility campaign, I would run this checklist.
                </p>
              </div>
              <div className="px-3.5 py-1.5 rounded-full bg-cyan-500/15 border border-cyan-500/30 text-xs font-mono text-cyan-300 font-bold whitespace-nowrap self-start sm:self-auto">
                {completedCount} / {totalChecklistCount} Completed
              </div>
            </div>

            {/* Technical Accessibility */}
            <div className="space-y-3 pt-2">
              <h3 className="font-heading font-bold text-lg text-cyan-400">
                Technical Accessibility
              </h3>
              <div className="space-y-2">
                {technicalChecklist.map((item, idx) => {
                  const id = `tech-${idx}`;
                  const isChecked = !!checkedItems[id];
                  return (
                    <button
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={`w-full text-left p-3 rounded-xl border flex items-center gap-3 transition-all cursor-pointer ${
                        isChecked
                          ? "bg-cyan-950/30 border-cyan-500/40 text-white"
                          : "bg-white/[0.02] border-white/[0.08] text-white/80 hover:bg-white/[0.05]"
                      }`}
                    >
                      <span className="shrink-0 text-cyan-400">
                        {isChecked ? (
                          <CheckSquare className="w-5 h-5 text-cyan-400" />
                        ) : (
                          <Square className="w-5 h-5 text-white/30" />
                        )}
                      </span>
                      <span className={`text-sm sm:text-base ${isChecked ? "line-through text-white/50" : ""}`}>
                        {item}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content Accessibility */}
            <div className="space-y-3 pt-4">
              <h3 className="font-heading font-bold text-lg text-indigo-400">
                Content Accessibility
              </h3>
              <div className="space-y-2">
                {contentChecklist.map((item, idx) => {
                  const id = `content-${idx}`;
                  const isChecked = !!checkedItems[id];
                  return (
                    <button
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={`w-full text-left p-3 rounded-xl border flex items-center gap-3 transition-all cursor-pointer ${
                        isChecked
                          ? "bg-indigo-950/30 border-indigo-500/40 text-white"
                          : "bg-white/[0.02] border-white/[0.08] text-white/80 hover:bg-white/[0.05]"
                      }`}
                    >
                      <span className="shrink-0 text-indigo-400">
                        {isChecked ? (
                          <CheckSquare className="w-5 h-5 text-indigo-400" />
                        ) : (
                          <Square className="w-5 h-5 text-white/30" />
                        )}
                      </span>
                      <span className={`text-sm sm:text-base ${isChecked ? "line-through text-white/50" : ""}`}>
                        {item}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* AI Retrieval Validation */}
            <div className="space-y-3 pt-4">
              <h3 className="font-heading font-bold text-lg text-emerald-400">
                AI Retrieval Validation
              </h3>
              <div className="space-y-2">
                {retrievalChecklist.map((item, idx) => {
                  const id = `retrieval-${idx}`;
                  const isChecked = !!checkedItems[id];
                  return (
                    <button
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={`w-full text-left p-3 rounded-xl border flex items-center gap-3 transition-all cursor-pointer ${
                        isChecked
                          ? "bg-emerald-950/30 border-emerald-500/40 text-white"
                          : "bg-white/[0.02] border-white/[0.08] text-white/80 hover:bg-white/[0.05]"
                      }`}
                    >
                      <span className="shrink-0 text-emerald-400">
                        {isChecked ? (
                          <CheckSquare className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <Square className="w-5 h-5 text-white/30" />
                        )}
                      </span>
                      <span className={`text-sm sm:text-base ${isChecked ? "line-through text-white/50" : ""}`}>
                        {item}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs sm:text-sm text-white/75">
                Want us to run this complete 4-layer technical &amp; AI retrieval checklist on your website?
              </p>
              <Link
                to="/ai-visibility-audit/"
                className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-400/40 text-cyan-300 hover:text-white text-xs font-mono font-bold uppercase tracking-wider transition-all"
              >
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span>Book Your AI Visibility Audit</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </section>

          <hr className="border-white/10 my-8" />

          {/* The Real Question Isn't “Can I Rank in AI?” */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              The Real Question Isn't “Can I Rank in AI?”
            </h2>

            <p>It is:</p>

            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-100 font-semibold text-lg not-italic">
              “Can AI systems access, understand and retrieve my content when it is relevant to a user's question?”
            </blockquote>

            <p>That changes how you approach AI SEO.</p>
            <p>
              Instead of immediately producing hundreds of AI-focused articles, start by validating the technical foundation.
            </p>

            <div className="p-3 rounded-xl bg-[#080d19] border border-cyan-500/30 font-mono text-xs sm:text-sm text-cyan-300 font-bold text-center overflow-x-auto">
              Crawl &rarr; Access &rarr; Discover &rarr; Understand &rarr; Retrieve &rarr; Cite
            </div>

            <p>Technical SEO removes barriers.</p>
            <p>Content quality and relevance create value.</p>
            <p>External signals can provide additional context.</p>
            <p>
              And ultimately, retrieval systems decide which sources are surfaced for a particular query.
            </p>
            <p>There is no single technical setting that guarantees an AI citation.</p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* Final Takeaway */}
          <section className="space-y-4">
            <h2 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight">
              Final Takeaway
            </h2>

            <p className="text-xl font-bold text-white">
              AI visibility doesn't start with AI.
            </p>
            <p>It starts with your website.</p>

            <p>Before asking:</p>
            <blockquote className="p-3 rounded-xl bg-[#0c1427] border-l-4 border-white/40 text-white/80 not-italic">
              “How do I get ChatGPT to cite my website?”
            </blockquote>

            <p>Ask:</p>
            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-cyan-400 text-cyan-200 font-semibold not-italic text-lg">
              “Can the systems I care about actually access, understand and retrieve what I've published?”
            </blockquote>

            <p>If the answer is no, fix the technical foundation first.</p>
            <p>If the answer is yes, then move to the next layer:</p>

            <div className="p-4 rounded-xl bg-[#080d19] border border-emerald-500/30 font-mono text-xs sm:text-sm text-emerald-300 font-bold text-center overflow-x-auto">
              Relevance &rarr; Content quality &rarr; Entity clarity &rarr; Evidence &rarr; Authority &rarr; Retrieval &rarr; Citation
            </div>

            <p>
              That's where AI visibility becomes more than just another SEO checklist.
            </p>
            <p>
              Once your technical infrastructure is accessible to AI bots, content relevance hinges on answering real customer objections. Learn how to discover these topics in my step-by-step guide on{" "}
              <Link
                to="/blog/how-to-do-keyword-research-for-ai-visibility/"
                className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
              >
                how to do keyword research for AI visibility
              </Link>
              .
            </p>
            <p>
              And that's where SEO, AEO and emerging AI-search optimization strategies start coming together.
            </p>
          </section>


        </article>

        {/* Related Technical Playbooks & Guides */}
        <section className="my-12 space-y-6">
          <div className="flex items-center justify-between pb-3 border-b border-white/10">
            <h3 className="font-heading font-bold text-xl text-white tracking-tight flex items-center gap-2">
              <Layers className="w-5 h-5 text-cyan-400" />
              <span>Related Technical Playbooks &amp; Case Studies</span>
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/blog/how-to-do-keyword-research-for-ai-visibility/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-cyan-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 uppercase">
                  Intent &amp; AI Visibility
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-cyan-400 transition-colors leading-snug">
                  How to Do Keyword Research for AI Visibility
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Customer-intent-first approach using GSC, Reddit, customer reviews, and sales conversations to build a Question Memory.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-cyan-400 font-semibold">
                <span>Read Intent Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/blog/how-to-measure-ai-search-visibility-for-free/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-indigo-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 uppercase">
                  Free Analytics Framework
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-indigo-400 transition-colors leading-snug">
                  How to Measure AI Search Visibility for Free
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Step-by-step 5-source framework using GSC, Bing Webmaster Tools, Server Logs, GA4, and Clarity without paid tools.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-indigo-400 font-semibold">
                <span>Read Free Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-purple-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-purple-500/10 text-purple-300 border border-purple-500/20 uppercase">
                  AEO / GEO Strategy
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-purple-400 transition-colors leading-snug">
                  Why Do I Rank on Google but Don't Appear in ChatGPT?
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Why Google rankings don't equal ChatGPT citations, how fan-out queries operate, and the 6-step AI visibility audit protocol.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-purple-400 font-semibold">
                <span>Read Strategy Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/how-to-audit-a-website-for-ai-visibility/"
              className="p-5 rounded-2xl bg-[#0c1222] border border-white/10 hover:border-emerald-500/40 transition-all group flex flex-col justify-between"
            >
              <div className="space-y-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 uppercase">
                  10-Point Technical Checklist
                </span>
                <h4 className="font-heading font-bold text-base text-white group-hover:text-emerald-400 transition-colors leading-snug">
                  How Do I Audit My Website for Better AI Visibility Across ChatGPT, Google AI Overviews and Claude?
                </h4>
                <p className="text-xs text-white/60 line-clamp-3">
                  Audit your website yourself with 10 practical checks covering bot crawling, indexability, JavaScript rendering parity, and citations.
                </p>
              </div>
              <div className="pt-4 mt-3 border-t border-white/[0.06] flex items-center justify-between text-xs text-emerald-400 font-semibold">
                <span>Read Audit Guide</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </section>

        {/* Author Bio Card */}
        <footer className="mt-12 pt-8 border-t border-white/10">
          <div className="p-6 rounded-2xl bg-[#0a0f1d] border border-white/10 flex flex-col sm:flex-row items-center sm:items-start gap-5 text-center sm:text-left">
            <img
              src="/narendra-workstation.jpg"
              alt="Kanna Narendra Kumar"
              className="w-20 h-20 rounded-2xl object-cover object-[center_20%] border border-cyan-500/40 shrink-0 shadow-xl"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "/narendra-avatar.jpg";
              }}
            />
            <div className="space-y-2 flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h4 className="font-heading font-bold text-lg text-white">
                    Written by Kanna Narendra Kumar
                  </h4>
                  <p className="text-xs font-mono text-cyan-400">
                    Senior SEO Specialist & AI Search Strategist
                  </p>
                </div>
                <a
                  href="https://www.linkedin.com/in/narendra-kumar-kanna/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-xs text-white/80 font-mono transition-colors self-center sm:self-auto"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>LinkedIn Profile</span>
                </a>
              </div>
              <p className="text-xs sm:text-sm text-white/75 leading-relaxed font-sans">
                Senior SEO Specialist &amp; AI Search Strategist with 8+ years scaling organic visibility and high-intent inbound pipeline for global B2B SaaS. Specialized in Technical SEO, enterprise migrations, and next-generation Answer Engine Optimization (AEO · GEO · HEO). Published at The Search &amp; Digital Growth Lab.
              </p>
            </div>
          </div>
        </footer>

        {/* Collaboration CTA Footer */}
        <section className="mt-10 p-8 sm:p-10 rounded-3xl bg-[#0c1222] border border-white/10 text-center space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 border border-cyan-500/30 flex items-center justify-center mx-auto text-cyan-400">
            <Zap className="w-6 h-6" />
          </div>
          <h3 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight uppercase">
            Need High-Impact Search &amp; AI Execution?
          </h3>
          <p className="text-sm text-white/60 max-w-xl mx-auto leading-relaxed">
            Whether you want to audit your website&apos;s 4-layer AI search readiness, fix crawler and JavaScript rendering barriers, or scale organic SQLs, let&apos;s connect directly.
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/ai-visibility-audit/"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-transform hover:-translate-y-0.5 cursor-pointer"
            >
              <Sparkles className="w-4 h-4" />
              <span>Get an AI Visibility Audit</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/[0.05] hover:bg-white/[0.1] border border-cyan-500/30 text-white text-xs font-bold uppercase tracking-wider transition-colors"
            >
              <span>Schedule Strategy Discussion</span>
              <ArrowUpRight className="w-4 h-4 text-cyan-400" />
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
